import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import BusinessIcon from '@mui/icons-material/Business'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Typography
} from '@mui/material'
import { useNavigate } from 'react-router-dom'

import routes from '../../pages/routes'
import Database from '../../types/Database'

interface OfficeCreationSuccessProps {
  office: Database.Office
  onCreateDesk?: () => void
}

/**
 * OfficeCreationSuccess - Component for displaying office creation success
 *
 * Shows success message, office information, and provides navigation options
 * for continuing to desk creation or returning to office management.
 *
 * Requirements: 1.5, 5.2, 7.1, 7.2
 */
export default function OfficeCreationSuccess({
  office,
  onCreateDesk
}: OfficeCreationSuccessProps) {
  const navigate = useNavigate()

  const handleCreateDesk = () => {
    if (onCreateDesk) {
      onCreateDesk()
    } else {
      // Navigate to desk creation with pre-selected office
      navigate(`/create-desk/${office.oid}`)
    }
  }

  const handleBackToProfile = () => {
    navigate(routes.profile)
  }

  return (
    <Box sx={{ mt: 4, textAlign: 'center' }}>
      {/* Success Icon and Message */}
      <CheckCircleIcon
        sx={{
          fontSize: 64,
          color: 'success.main',
          mb: 2
        }}
      />

      <Typography
        variant='h4'
        component='h1'
        gutterBottom
        data-testid='office-created-success-title'
      >
        Office Created Successfully!
      </Typography>

      <Typography
        variant='body1'
        color='text.secondary'
        sx={{ mb: 4 }}
        data-testid='office-created-success-message'
      >
        Your office "{office.name}" has been created and you are now the owner.
      </Typography>

      {/* Office Information Card */}
      <Card
        sx={{
          maxWidth: 500,
          mx: 'auto',
          mb: 4,
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
        }}
      >
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <BusinessIcon fontSize='small' />
            </Avatar>
            <Typography variant='h6' component='h2' sx={{ fontWeight: 600 }}>
              Office Details
            </Typography>
          </Box>

          <Box sx={{ textAlign: 'left' }}>
            <Typography
              variant='body2'
              color='text.secondary'
              data-testid='office-name-display'
            >
              <strong>Name:</strong> {office.name}
            </Typography>

            {office.description && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1 }}
                data-testid='office-description-display'
              >
                <strong>Description:</strong> {office.description}
              </Typography>
            )}

            {office.address && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1 }}
                data-testid='office-address-display'
              >
                <strong>Address:</strong> {office.address}
              </Typography>
            )}

            {office.contactInfo && (
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{ mt: 1 }}
                data-testid='office-contact-display'
              >
                <strong>Contact:</strong> {office.contactInfo}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}
      >
        <Button
          variant='contained'
          startIcon={<AddIcon />}
          onClick={handleCreateDesk}
          data-testid='create-desk-button'
          sx={{
            py: 1.5,
            px: 3,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 8px 24px rgba(102, 126, 234, 0.6)'
            }
          }}
        >
          Create Desk
        </Button>

        <Button
          variant='outlined'
          startIcon={<ArrowBackIcon />}
          onClick={handleBackToProfile}
          data-testid='back-to-profile-button'
          sx={{
            py: 1.5,
            px: 3,
            borderRadius: 2,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }
          }}
        >
          Back to Profile
        </Button>
      </Box>
    </Box>
  )
}
